import { t } from 'i18next';
import React, { FC } from 'react';
import { IoPerson } from 'react-icons/io5';

interface WidgetHeaderProps {
  shoppingPreferenceLabel?: string;
  brandLogoUrl?: string;
  productImageUrl?: string;
  centerImageUrl?: string;
  productGenderLabel?: 'male' | 'female' | 'unknown';  // Product gender from API
  userGenderLabel?: 'male' | 'female' | undefined;     // User's chosen/actual gender
  genderSpecific?: 'false' | 'm' | 'f';                // Brand gender setting
  currentItem?: any;
  isAuthenticated?: boolean;
  isOnboarded?: boolean;
  onGenderToggle?: () => void; // Callback to toggle gender
}

const WidgetHeader: FC<WidgetHeaderProps> = ({ 
  shoppingPreferenceLabel, 
  brandLogoUrl, 
  centerImageUrl, 
  productGenderLabel,
  userGenderLabel,
  genderSpecific,
  currentItem,
  isAuthenticated,
  isOnboarded,
  onGenderToggle
}) => {
  // Debug logging for WidgetHeader props
  React.useEffect(() => {
    console.debug('🔍 [WIDGET_HEADER] WidgetHeader props:', {
      productGenderLabel,
      userGenderLabel,
      genderSpecific,
      isAuthenticated,
      isOnboarded
    });
  }, [productGenderLabel, userGenderLabel, genderSpecific, isAuthenticated, isOnboarded]);

  // console.debug the current item's gender_specific property
  React.useEffect(() => {
    if (currentItem && currentItem.gender_specific !== undefined) {
      console.debug('Current item gender_specific:', currentItem.gender_specific);
    } else if (genderSpecific) {
      console.debug('Current item gender_specific (from props):', genderSpecific);
    }
  }, [currentItem, genderSpecific]);

  // Determine the gender display label based on user's gender (priority) or product gender (fallback)
  const getGenderDisplayLabel = () => {
    // Priority 1: Use user's chosen gender if available
    if (userGenderLabel === 'male') return 'Menswear';
    if (userGenderLabel === 'female') return 'Womenswear';
    
    // Priority 2: Use product gender if user hasn't chosen yet
    if (productGenderLabel === 'male') return 'Menswear';
    if (productGenderLabel === 'female') return 'Womenswear';
    
    // Priority 3: Fallback to genderSpecific prop
    if (genderSpecific === 'm') return 'Menswear';
    if (genderSpecific === 'f') return 'Womenswear';
    
    // Default fallback
    return 'Menswear';
  };

  // Badge visibility logic according to your specification:
  // - if gender_specific==='false' && user_gender_label==='unknown' (or undefined) => shouldShowBadge='false'
  // - if gender_specific==='false' && user_gender_label!='unknown' (or undefined) => shouldShowBadge='true'  
  // - if gender_specific!='false' && user_gender_label==='unknown' (or undefined) => shouldShowBadge='true'
  // - if gender_specific!='false' && user_gender_label!='unknown' (or undefined) => shouldShowBadge='true'
  
  const isUserGenderUnknown = userGenderLabel === undefined;
  
  let shouldShowGenderBadge = false;
  
  if (genderSpecific === 'false') {
    // Brand is non-gender-specific
    if (isUserGenderUnknown) {
      shouldShowGenderBadge = false; // Case 1: non-specific brand + no user gender
    } else {
      shouldShowGenderBadge = true;  // Case 2: non-specific brand + user has gender
    }
  } else {
    // Brand is gender-specific (m or f)
    shouldShowGenderBadge = true;    // Case 3 & 4: specific brand always shows badge
  }

  React.useEffect(() => {
    console.debug('🔍 [WIDGET_HEADER] Badge visibility decision:', {
      genderSpecific,
      userGenderLabel,
      isUserGenderUnknown,
      shouldShowGenderBadge
    });
  }, [genderSpecific, userGenderLabel, isUserGenderUnknown, shouldShowGenderBadge]);

  // Handle gender badge click
  const handleGenderBadgeClick = () => {
    onGenderToggle?.();
  };
  return (
    <div className="pop-relative pop-h-full pop-w-full pop-flex pop-flex-col pop-items-center pop-justify-between pop-flex-none">
	    <img
        src={`${process.env.REACT_APP_GCP_BUCKET_URL}/images/popsize-logo.svg`}
        alt="popsize-logo"
        className="pop-absolute pop-top-1 pop-left-0 pop-w-logo"
      />

      {/* Centered Brand Logo */}
      {brandLogoUrl ? (
        <div className="pop-border-1 pop-max-h-[33%]">
          <img
        src={brandLogoUrl}
        alt="brand-logo"
        className="pop-h-12 pop-w-auto pop-max-h-12"
          />
        </div>
      ) : (
        <div className="pop-h-12 pop-w-auto" /> // Placeholder to maintain layout
      )}

      {/* Horizontal Row: Rectangle Box + Gender Badge */}
      <div className="pop-flex pop-flex-row pop-items-center pop-justify-between pop-w-full pop-h-[50%]">
        {/* Rectangle Box */}
        <div className="pop-flex pop-w-full pop-h-full pop-bg-transparent pop-rounded-xl pop-border-0.5 pop-border-popsizePrimary20 pop-shadow-lg pop-items-center">
          {/* Left Image */}
          <div className="pop-w-auto pop-h-full pop-p-2">
            {centerImageUrl && (
              <img
                src={centerImageUrl}
                alt="Product image"
                className="pop-w-full pop-h-full pop-object-contain"
              />
            )}
          </div>

          {/* Right Text */}
          <div className="pop-flex-1 pop-text-popsizePrimary90 pop-text-xs pop-sm:pop-text-xxs pop-leading-snug">
            <p>{t(`widget_header_text`)}</p>
          </div>
        </div>

			{/* Gender Badge - Only show if genderLabel is defined */}
			{shouldShowGenderBadge && (
				<button 
					onClick={handleGenderBadgeClick}
					className="pop-ml-4 pop-py-2 pop-px-4 pop-h-full pop-bg-popsizeSecondary10 pop-text-popsizePrimary90 pop-border-1 pop-border-popsizeSecondary90 pop-rounded-full pop-flex pop-flex-col pop-items-center pop-justify-center pop-shadow-lg hover:pop-bg-popsizeSecondary20 hover:pop-border-popsizeSecondary90 pop-transition-colors pop-cursor-pointer"
				>
					<IoPerson size={12} className="pop-mb-1 pop-text-popsizeSecondary90" />
					<span className="pop-text-center pop-text-xxs pop-underline pop-font-semibold">{t(getGenderDisplayLabel())}</span>
				</button>
			)}
      </div>
    </div>
  );
};

export default WidgetHeader;